/* istanbul ignore file */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import { withTheme } from 'react-jss';
import { withProps } from '@bingads-webui-react/with-props';
import { withDelayedStyles } from '@bingads-webui-campaign-react/common-utils';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { getSearchCreationV2Style } from '../styles/campaign-creation-search-v2-styles';
import { withCreationHeader } from '../helper';

const getSearchCampaignWizardAsync = () => import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/search-campaign-wizard')
  .then(({ SearchCampaignCreationWizard }) => ({ default: SearchCampaignCreationWizard }));

const getDelayedCreationStyles = () => (
  import(/* webpackMode: 'eager' */'../styles')
    .then(({ getEntityCreationStyle }) => ({
      getDelayedStyles: getEntityCreationStyle,
    }))
);

const CampaignCreationSearchV2 = (props) => {
  const {
    adExtensionsDynamicFeaturesLoader,
    permissions,
    theme,
    scope,
    instrumentation,
    i18n,
  } = props;

  const SearchCampaignCreationWizard = useMemo(() => React.lazy((() => getSearchCampaignWizardAsync())), []);

  const dynamicOptionsLoader = useCallback(
    () => Promise.props({
      adExtensionsDynamicFeatures: adExtensionsDynamicFeaturesLoader({
        permissions,
        theme,
        scope,
        instrumentation,
        i18n,
      }),
      adExtensionsAssociationsFactory:
        import(/* webpackChunkName: "campaign-adgroup-creation" */'@bingads-webui-campaign-react/ad-extensions-associations-factory'),
    })
      .then(({ adExtensionsDynamicFeatures, adExtensionsAssociationsFactory }) => ({
        adExtensionsDynamicFeatures,
        adExtensionsAssociationsFactory,
      }))
      .then(dynamicOptions => ({
        default: withProps({ dynamicOptions }, SearchCampaignCreationWizard),
      })),
    [adExtensionsDynamicFeaturesLoader, permissions, theme, scope, instrumentation, i18n, SearchCampaignCreationWizard]
  );

  const LazyView = useMemo(() => React.lazy(dynamicOptionsLoader), [dynamicOptionsLoader]);

  return (
    <React.Suspense fallback={<ProgressiveLoadingBlock />}>
      <LazyView
        adExtensionsDynamicFeaturesLoader={adExtensionsDynamicFeaturesLoader}
        {...props}
      />
    </React.Suspense>
  );
};

CampaignCreationSearchV2.propTypes = {
  adExtensionsDynamicFeaturesLoader: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  theme: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({}).isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
};

export const StyledCampaignCreationSearchV2 =
  withDelayedStyles(withCreationHeader(withTheme(CampaignCreationSearchV2), undefined, undefined, undefined, true), getSearchCreationV2Style, getDelayedCreationStyles, 'campaign_create_search_v2_loaded');
